/* Sprint 2O — Driver PWA: My Shifts + Availability registration.
   Mobile-first. Driver registers availability slots and confirms/declines planned shifts. */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Calendar, Check, X, Clock, MapPin, ChevronLeft, ChevronRight,
  Bell, AlertTriangle, ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function driverApi() {
  const token = localStorage.getItem("ema_driver_token");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function fmtDate(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

const SHIFT_STATUS = {
  published: { label: "Afventer dit svar", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  confirmed: { label: "Bekræftet", cls: "bg-green-100 text-green-800 border-green-300" },
  declined: { label: "Afvist", cls: "bg-red-100 text-red-800 border-red-300" },
  in_progress: { label: "I gang", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  completed: { label: "Afsluttet", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

const DAY_NAMES_SHORT = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];

export default function DriverVagtplan() {
  const [activeTab, setActiveTab] = useState("shifts");
  const [shifts, setShifts] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [slots, setSlots] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [declineModal, setDeclineModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = fmtDate(new Date());
      const future = fmtDate(addDays(new Date(), 30));
      const [sRes, aRes] = await Promise.all([
        driverApi().get(`/driver/my-shifts?date_from=${today}&date_to=${future}`),
        driverApi().get(`/driver/availability?date_from=${fmtDate(weekStart)}&date_to=${fmtDate(addDays(weekStart, 13))}`),
      ]);
      setShifts(sRes.data.items || []);
      setAvailability(aRes.data.items || []);
      setSlots(aRes.data.slots || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        window.location.href = "/driver/login";
        return;
      }
      toast.error("Kunne ikke hente vagtplan");
    } finally { setLoading(false); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);

  const confirmShift = async (id) => {
    try {
      await driverApi().post(`/driver/my-shifts/${id}/confirm`);
      toast.success("Skift bekræftet");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bekræftelse fejlede");
    }
  };

  const declineShift = async (id, reason) => {
    if (!reason || !reason.trim()) { toast.error("Begrundelse er påkrævet"); return; }
    try {
      await driverApi().post(`/driver/my-shifts/${id}/decline`, { reason: reason.trim() });
      toast.success("Skift afvist - admin er informeret");
      setDeclineModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Afvisning fejlede");
    }
  };

  const toggleAvailability = async (date, slot, currentStatus) => {
    try {
      await driverApi().post("/driver/availability", {
        date, slot, is_available: !currentStatus,
      });
      toast.success(currentStatus ? "Markeret som ikke-disponibel" : "Markeret som disponibel");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    }
  };

  const availabilityMap = React.useMemo(() => {
    const m = {};
    availability.forEach((a) => {
      m[`${a.date}|${a.slot}`] = a;
    });
    return m;
  }, [availability]);

  const pendingShifts = shifts.filter((s) => s.status === "published");

  return (
    <div className="min-h-screen bg-brand-bg2 pb-20" data-testid="driver-vagtplan">
      {/* Header */}
      <div className="bg-brand-dark text-white px-4 py-4 flex items-center justify-between sticky top-0 z-10">
        <Link to="/driver/my-routes" className="text-white flex items-center gap-1 text-sm" data-testid="back-to-routes">
          <ArrowLeft size={16}/> Mine ruter
        </Link>
        <h1 className="font-display font-bold text-lg flex items-center gap-2">
          <Calendar size={20}/> Vagtplan
        </h1>
        <div className="w-20" />
      </div>

      {/* Pending banner */}
      {pendingShifts.length > 0 && (
        <div className="bg-blue-50 border-b-2 border-blue-300 px-4 py-3" data-testid="driver-pending-banner">
          <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
            <Bell size={16}/> {pendingShifts.length} {pendingShifts.length === 1 ? "skift afventer" : "skift afventer"} din bekræftelse
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white border-b border-brand sticky top-[60px] z-9">
        <div className="flex">
          <button
            onClick={() => setActiveTab("shifts")} data-testid="driver-tab-shifts"
            className={`flex-1 py-3 text-sm font-bold tracking-wider uppercase ${activeTab === "shifts" ? "border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}
          >
            Mine skift ({shifts.length})
          </button>
          <button
            onClick={() => setActiveTab("availability")} data-testid="driver-tab-availability"
            className={`flex-1 py-3 text-sm font-bold tracking-wider uppercase ${activeTab === "availability" ? "border-b-2 border-brand-red text-brand-red" : "text-brand-text2"}`}
          >
            Disponibilitet
          </button>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-brand-text2 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={16}/> Indlæser...
        </div>
      )}

      {/* SHIFTS TAB */}
      {!loading && activeTab === "shifts" && (
        <div className="p-4 space-y-3" data-testid="driver-shifts-list">
          {shifts.length === 0 && (
            <div className="bg-white border border-brand p-6 text-center text-brand-text2">
              <Calendar size={32} className="mx-auto mb-2 opacity-50"/>
              Ingen planlagte skift de næste 30 dage.
            </div>
          )}
          {shifts.map((s) => (
            <ShiftItem
              key={s.id} shift={s}
              onConfirm={() => confirmShift(s.id)}
              onDecline={() => setDeclineModal(s)}
            />
          ))}
        </div>
      )}

      {/* AVAILABILITY TAB */}
      {!loading && activeTab === "availability" && (
        <div className="p-4" data-testid="driver-availability-grid">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              data-testid="avail-prev-week"
              className="bg-white border border-brand px-3 py-2 text-xs flex items-center gap-1"
            >
              <ChevronLeft size={14}/>
            </button>
            <div className="text-sm font-bold text-brand-dark">
              {fmtDate(weekStart)} – {fmtDate(addDays(weekStart, 6))}
            </div>
            <button
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              data-testid="avail-next-week"
              className="bg-white border border-brand px-3 py-2 text-xs flex items-center gap-1"
            >
              <ChevronRight size={14}/>
            </button>
          </div>

          <p className="text-xs text-brand-text2 mb-3">
            Tryk for at markere dig disponibel for et tidsrum. Admin ser kun de tidsrum hvor du er disponibel.
          </p>

          <div className="space-y-2">
            {Array.from({ length: 7 }).map((_, i) => {
              const d = addDays(weekStart, i);
              const dStr = fmtDate(d);
              const today = fmtDate(new Date()) === dStr;
              return (
                <div key={dStr} className={`bg-white border ${today ? "border-brand-red border-2" : "border-brand"} p-3`} data-testid={`avail-day-${dStr}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-sm text-brand-dark">
                      {DAY_NAMES_SHORT[i]} {d.getDate()}/{d.getMonth() + 1}
                    </div>
                    {today && <span className="text-xs text-brand-red font-bold">I dag</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {slots.map((slot) => {
                      const key = `${dStr}|${slot.key}`;
                      const av = availabilityMap[key];
                      const isOn = av?.is_available === true;
                      return (
                        <button
                          key={slot.key}
                          onClick={() => toggleAvailability(dStr, slot.key, isOn)}
                          data-testid={`avail-slot-${dStr}-${slot.key}`}
                          className={`text-xs p-2 border ${isOn ? "bg-green-50 border-green-300 text-green-800" : "bg-brand-bg2 border-brand text-brand-text2"}`}
                        >
                          <div className="font-bold">{isOn ? "✓ " : ""}{slot.label}</div>
                          <div className="text-[10px] opacity-70">{slot.start}-{slot.end}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {declineModal && (
        <DeclineModal
          shift={declineModal}
          onConfirm={(reason) => declineShift(declineModal.id, reason)}
          onClose={() => setDeclineModal(null)}
        />
      )}
    </div>
  );
}

function ShiftItem({ shift, onConfirm, onDecline }) {
  const badge = SHIFT_STATUS[shift.status] || { label: shift.status, cls: "bg-slate-100" };
  const d = new Date(shift.date + "T00:00:00");
  const dayName = DAY_NAMES_SHORT[(d.getDay() + 6) % 7];
  return (
    <div
      className={`bg-white border-2 p-4 ${shift.status === "published" ? "border-blue-400" : "border-brand"}`}
      data-testid={`driver-shift-${shift.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-brand-dark text-base">
          {dayName} {d.getDate()}/{d.getMonth() + 1}
        </div>
        <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${badge.cls}`}>
          {badge.label}
        </span>
      </div>
      <div className="flex items-center gap-3 text-sm text-brand-text2 mb-2">
        <div className="flex items-center gap-1"><Clock size={12}/> {shift.start_time}-{shift.end_time}</div>
        <div>•</div>
        <div>{shift.planned_hours}t</div>
      </div>
      {shift.area_preference && (
        <div className="text-xs text-brand-text2 flex items-center gap-1 mb-2">
          <MapPin size={11}/> {shift.area_preference}
        </div>
      )}
      {shift.notes && (
        <div className="text-xs text-brand-text2 bg-brand-bg2 p-2 mb-3">
          📝 {shift.notes}
        </div>
      )}
      {(shift.route_ids || []).length > 0 && (
        <div className="text-xs text-brand-dark bg-emerald-50 border border-emerald-200 p-2 mb-3" data-testid={`driver-shift-route-${shift.id}`}>
          🔗 Tilknyttet rute(r): <b>{shift.route_ids.join(", ")}</b>
          {shift.approved_payable_minutes ? <> · Akkord: <b>{(shift.approved_payable_minutes/60).toFixed(2)}t</b></> : null}
        </div>
      )}
      {shift.decline_reason && (
        <div className="text-xs text-red-700 bg-red-50 p-2 mb-3 border border-red-200">
          Du afviste: {shift.decline_reason}
        </div>
      )}
      {shift.status === "published" && (
        <div className="flex gap-2 mt-3" data-testid={`driver-shift-actions-${shift.id}`}>
          <button
            onClick={onConfirm}
            data-testid={`driver-shift-confirm-${shift.id}`}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 text-sm font-bold tracking-wider uppercase flex items-center justify-center gap-1"
          >
            <Check size={14}/> Bekræft
          </button>
          <button
            onClick={onDecline}
            data-testid={`driver-shift-decline-${shift.id}`}
            className="flex-1 bg-white border-2 border-red-300 text-red-700 hover:bg-red-50 py-2 text-sm font-bold tracking-wider uppercase flex items-center justify-center gap-1"
          >
            <X size={14}/> Afvis
          </button>
        </div>
      )}
    </div>
  );
}

function DeclineModal({ shift, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-2" data-testid="driver-decline-modal">
      <div className="bg-white border border-brand max-w-md w-full">
        <div className="bg-red-700 text-white px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold flex items-center gap-2">
            <AlertTriangle size={18}/> Afvis skift?
          </div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm">
            Skift: <b>{shift.date}</b> kl. <b>{shift.start_time}-{shift.end_time}</b>
          </div>
          <p className="text-xs text-brand-text2">
            Du skal angive en begrundelse. Admin bliver underrettet om afvisningen og kan omfordele skiftet.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Fx: Sygdom, anden aftale, transportproblem..."
            data-testid="driver-decline-reason"
            className="w-full border border-brand p-3 text-sm focus:border-brand-red focus:outline-none"
          />
        </div>
        <div className="border-t border-brand p-4 flex justify-end gap-3 bg-brand-bg2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-brand-text2">Fortryd</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim()}
            data-testid="driver-decline-submit"
            className="bg-red-700 hover:bg-red-800 text-white px-5 py-2 text-sm font-bold tracking-wider uppercase disabled:opacity-50"
          >
            Bekræft afvisning
          </button>
        </div>
      </div>
    </div>
  );
}
